import React, { createContext, useContext, useState, useCallback } from 'react';
import { DisclaimerFeatureModel } from '../../fetch-data/graphql/types';
import { useStore } from '../../store/store';

interface DisclaimerContextType {
  openDisclaimer: (disclaimerId: string, displayName?: string) => void;
  closeDisclaimer: () => void;
  activeDisclaimer: DisclaimerFeatureModel | null;
  activeDisclaimerDisplayName: string | null;
  isOpen: boolean;
}

const DisclaimerContext = createContext<DisclaimerContextType | undefined>(
  undefined
);

export const useDisclaimer = () => {
  const context = useContext(DisclaimerContext);
  if (!context) {
    throw new Error('useDisclaimer must be used within DisclaimerProvider');
  }
  return context;
};

interface DisclaimerProviderProps {
  children: React.ReactNode;
}

export const DisclaimerProvider: React.FC<DisclaimerProviderProps> = ({
  children,
}) => {
  const [state] = useStore();
  const { allDisclaimers } = state.qsg;

  const [activeDisclaimerId, setActiveDisclaimerId] = useState<string | null>(
    null
  );
  const [activeDisclaimerDisplayName, setActiveDisclaimerDisplayName] =
    useState<string | null>(null);

  const openDisclaimer = useCallback(
    (disclaimerId: string, displayName?: string) => {
      setActiveDisclaimerId(disclaimerId);
      setActiveDisclaimerDisplayName(displayName || null);
    },
    []
  );

  const closeDisclaimer = useCallback(() => {
    setActiveDisclaimerId(null);
    setActiveDisclaimerDisplayName(null);
  }, []);

  const activeDisclaimer = activeDisclaimerId
    ? allDisclaimers.find((d) => d.disclaimerId === activeDisclaimerId) || null
    : null;

  return (
    <DisclaimerContext.Provider
      value={{
        openDisclaimer,
        closeDisclaimer,
        activeDisclaimer,
        activeDisclaimerDisplayName,
        isOpen: Boolean(activeDisclaimerId),
      }}
    >
      {children}
    </DisclaimerContext.Provider>
  );
};
